import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Primitives";
import { RichTextEditor } from "@/components/RichTextEditor";
import { ROLE_OPTIONS, ROLE_LABELS, TOOLS } from "@/config/tools";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, ShieldCheck, Upload, Loader2, Search, Eye, Mail, CalendarClock } from "lucide-react";

const ASSIGNABLE = TOOLS.filter((t) => t.key !== "users");
const TOOL_NAMES = Object.fromEntries(TOOLS.map((t) => [t.key, t.name]));
const EMPTY = { email: "", name: "", phone: "", headshot: "", password: "", role: "agent", tools: [], access: {}, can_manage_users: false, tm_role: "admin", weekend_task_digest: true };
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

export default function AdminUsers() {
  const { user: me, impersonate } = useAuth();
  const navigate = useNavigate();
  const isAdmin = me.role === "admin";
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [resendingWelcome, setResendingWelcome] = useState(false);
  const [search, setSearch] = useState("");
  const [defaultSig, setDefaultSig] = useState("");
  const [savingSig, setSavingSig] = useState(false);

  const q = search.trim().toLowerCase();
  const filteredUsers = q
    ? users.filter((u) => `${u.name || ""} ${u.email || ""} ${u.phone || ""}`.toLowerCase().includes(q))
    : users;

  const load = async () => setUsers((await api.get("/admin/users")).data);
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/settings/email-signature").then((r) => setDefaultSig(r.data.default_signature || "")).catch(() => {}); }, []);

  const saveDefaultSig = async () => {
    setSavingSig(true);
    try {
      const { data } = await api.put("/settings/email-signature", { default_signature: defaultSig });
      setDefaultSig(data.default_signature || "");
      toast.success("Team default signature saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingSig(false); }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, name: u.name, phone: u.phone || "", headshot: u.headshot || "", password: "", role: u.role, tools: (u.tools || []).filter((t) => t !== "users"), access: { ...(u.access || {}) }, can_manage_users: !!u.can_manage_users, tm_role: u.tm_role || "admin", weekend_task_digest: u.weekend_task_digest !== false });
    setOpen(true);
  };
  const toggleTool = (k) => setForm((f) => {
    const has = f.tools.includes(k);
    const tools = has ? f.tools.filter((x) => x !== k) : [...f.tools, k];
    const access = { ...f.access };
    if (has) delete access[k]; else access[k] = access[k] || (k === "transactions" ? "write" : "read");
    return { ...f, tools, access };
  });
  const setLevel = (k, lvl) => setForm((f) => ({ ...f, access: { ...f.access, [k]: lvl } }));
  const setTmRole = (v) => setForm((f) => ({ ...f, tm_role: v, access: { ...f.access, transactions: "write" } }));

  const uploadHeadshot = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, headshot: data.url }));
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { name: form.name, phone: form.phone, headshot: form.headshot, role: form.role, tools: form.tools, access: form.access, can_manage_users: form.can_manage_users, tm_role: form.tm_role, weekend_task_digest: form.weekend_task_digest };
        if (form.password) payload.password = form.password;
        await api.patch(`/admin/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/admin/users", form);
        toast.success("User created");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const resendWelcome = async () => {
    if (!editing) return;
    if (!window.confirm(`Reset ${editing.name || editing.email}'s password and email them a new temporary password and login link?`)) return;
    setResendingWelcome(true);
    try {
      await api.post(`/admin/users/${editing.id}/resend-welcome`);
      toast.success(`Welcome email sent to ${editing.email} with a new temporary password`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send the welcome email");
    } finally { setResendingWelcome(false); }
  };
  const remove = async (u) => {    try { await api.delete(`/admin/users/${u.id}`); toast.success("User removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const roleOpts = isAdmin ? ROLE_OPTIONS : ROLE_OPTIONS.filter((r) => r.value !== "admin");
  const adminSelected = form.role === "admin";

  return (
    <div data-testid="users-page">
      <PageHeader
        title="User Management"
        subtitle="Add team members and grant each person access to exactly the sections they need. You can also let trusted users manage other users."
        action={
          <button data-testid="add-user-button" onClick={openNew} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#F7941D] text-white font-semibold hover:bg-[#E08312] transition-colors duration-200">
            <Plus className="w-4 h-4" /> Add User
          </button>
        }
      />

      <div className="bg-white border border-[#E2E6F0] rounded-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E2E6F0] flex items-center justify-between gap-4 flex-wrap">
          <h3 className="font-serif text-xl">Team Members</h3>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 text-[#999] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              data-testid="user-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email or phone…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-full border border-[#E2E6F0] bg-white focus:outline-none focus:border-[#F7941D] focus:ring-1 focus:ring-[#F7941D] transition-colors"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#E2E6F0] text-left text-xs tracking-[0.15em] uppercase text-[#555]">
              <th className="px-6 py-3 font-semibold">Name</th><th className="px-6 py-3 font-semibold">Contact</th>
              <th className="px-6 py-3 font-semibold">Section Access</th><th className="px-6 py-3 font-semibold">Admin</th>
              <th className="px-6 py-3 font-semibold text-right">Actions</th>
            </tr></thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-[#F0F2F8] hover:bg-[#F8FAFE] transition-colors duration-200">
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-3">
                      {u.headshot ? <img src={absUrl(u.headshot)} alt="" className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-[#F7941D] text-white flex items-center justify-center text-xs font-semibold shrink-0">{(u.name || u.email).split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}</div>}
                      <span className="font-medium text-[#111]">{u.name}{u.id === me.id && <span className="ml-2 text-xs text-[#F7941D]">(you)</span>}</span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-[#555]">{u.email}{u.phone && <div className="text-xs text-[#999]">{u.phone}</div>}</td>
                  <td className="px-6 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(u.access || {}).length
                        ? Object.entries(u.access).map(([t, lvl]) => <StatusBadge key={t} tone={lvl === "write" ? "green" : "orange"}>{TOOL_NAMES[t]} · {lvl === "write" ? "RW" : "RO"}</StatusBadge>)
                        : (u.role === "admin" ? <StatusBadge tone="emerald">All sections</StatusBadge> : <span className="text-xs text-[#999]">No sections</span>)}
                    </div>
                  </td>
                  <td className="px-6 py-3">{(u.role === "admin" || u.can_manage_users) ? <StatusBadge tone="orange">User Admin</StatusBadge> : <span className="text-xs text-[#999]">—</span>}</td>
                  <td className="px-6 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {u.role !== "admin" && u.id !== me.id && !me.impersonator && (
                        <button
                          data-testid={`impersonate-user-${u.id}`}
                          onClick={async () => {
                            try { await impersonate(u.id); toast.success(`Now viewing as ${u.name || u.email}`); navigate("/"); }
                            catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                          }}
                          title="View as this user"
                          className="p-2 rounded-sm text-[#555] hover:bg-[#EAF0FF] hover:text-[#1F3C88] transition-colors duration-200"
                        ><Eye className="w-4 h-4" /></button>
                      )}
                      <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} className="p-2 rounded-sm text-[#555] hover:bg-[#F0F2F8] hover:text-[#1F3C88] transition-colors duration-200"><Pencil className="w-4 h-4" /></button>
                      {u.id !== me.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button data-testid={`delete-user-${u.id}`} className="p-2 rounded-sm text-[#555] hover:bg-[#F6E4E4] hover:text-[#992B2B] transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-white">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="font-serif text-2xl">Remove {u.name}?</AlertDialogTitle>
                              <AlertDialogDescription>This revokes their access immediately. This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction data-testid={`confirm-delete-${u.id}`} onClick={() => remove(u)} className="bg-[#992B2B] hover:bg-[#7d2222]">Remove</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr><td colSpan={5} data-testid="no-users-found" className="px-6 py-10 text-center text-sm text-[#999]">No team members match "{search}".</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div data-testid="team-default-signature-card" className="bg-white border border-[#E2E6F0] rounded-sm overflow-hidden mt-6">
        <div className="px-6 py-4 border-b border-[#E2E6F0]">
          <h3 className="font-serif text-xl">Team Default Email Signature</h3>
          <p className="text-sm text-[#666] mt-1">Used on outbound transaction emails when the sender hasn't set their own signature (each person can set theirs from the sidebar "Email signature" button).</p>
        </div>
        <div className="p-6 space-y-3">
          <RichTextEditor
            value={defaultSig}
            onChange={setDefaultSig}
            placeholder="e.g. The MattC Team · eXp Realty · (513) 555-0100 · mattcteam.com"
            testid="default-signature-editor"
          />
          <div className="flex justify-end">
            <button data-testid="save-default-signature-button" onClick={saveDefaultSig} disabled={savingSig}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1F3C88] text-white font-semibold hover:bg-[#152A61] transition-colors duration-200 disabled:opacity-60">
              {savingSig ? "Saving…" : "Save default signature"}
            </button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="user-form">
            <div className="flex items-center gap-4">
              {form.headshot ? <img src={absUrl(form.headshot)} alt="" className="w-16 h-16 rounded-full object-cover" /> : <div className="w-16 h-16 rounded-full bg-[#EAF0FB] flex items-center justify-center text-[#1F3C88]"><Upload className="w-5 h-5" /></div>}
              <div>
                <label data-testid="user-headshot-upload" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E2E6F0] text-sm text-[#1F3C88] cursor-pointer hover:bg-[#F8FAFE]">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {form.headshot ? "Change photo" : "Upload photo"}
                  <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={uploadHeadshot} />
                </label>
                {form.headshot && <button type="button" data-testid="user-headshot-remove" onClick={() => setForm({ ...form, headshot: "" })} className="ml-2 text-sm text-[#992B2B] hover:underline">Remove</button>}
              </div>
            </div>
            <Field label="Full name"><input required data-testid="user-name-input" className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email"><input required type="email" disabled={!!editing} data-testid="user-email-input" className={`${inp} ${editing ? "opacity-60" : ""}`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label="Phone"><input data-testid="user-phone-input" className={inp} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            </div>
            <Field label={editing ? "New password (leave blank to keep)" : "Password"}>
              <input required={!editing} type="password" data-testid="user-password-input" className={inp} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </Field>
            <Field label="Role (label)">
              <select data-testid="user-role-select" className={inp} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {roleOpts.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </Field>

            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-semibold text-[#555] block mb-2">Section Access</label>
              {adminSelected && <div className="text-xs text-[#555] bg-[#EAF0FB] rounded-sm p-2.5 mb-2">Administrators can reach every section. Set each to Read Only or Read/Write below.</div>}
              <div className="space-y-2">
                {ASSIGNABLE.map((t) => {
                  const on = adminSelected || form.tools.includes(t.key);
                  const lvl = form.access[t.key] || (adminSelected ? "write" : "read");
                  return (
                    <div key={t.key} className={`rounded-sm border px-3 py-2.5 transition-colors duration-200 ${on ? "border-[#F7941D] bg-[#FEF3E7]" : "border-[#E2E6F0]"}`}>
                      <div className="flex items-center justify-between gap-3">
                        <label data-testid={`tool-check-${t.key}`} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={on} disabled={adminSelected} onChange={() => toggleTool(t.key)} className="accent-[#F7941D]" />
                          <span className="text-sm text-[#111]">{t.name}</span>
                        </label>
                        {on && t.key === "transactions" && (
                          <div className="flex rounded-full border border-[#E2E6F0] overflow-hidden text-xs shrink-0">
                            {[["coordinator", "Coordinator"], ["admin", "Admin"]].map(([v, label]) => (
                              <button type="button" key={v} data-testid={`tmrole-${v}`} onClick={() => setTmRole(v)}
                                className={`px-3 py-1 font-semibold transition-colors duration-200 ${(form.tm_role || "admin") === v ? "bg-[#1F3C88] text-white" : "bg-white text-[#575755] hover:bg-[#F8FAFE]"}`}>
                                {label}
                              </button>
                            ))}
                          </div>
                        )}
                        {on && t.key !== "transactions" && (
                          <div className="flex rounded-full border border-[#E2E6F0] overflow-hidden text-xs shrink-0">
                            {["read", "write"].map((v) => (
                              <button type="button" key={v} data-testid={`level-${t.key}-${v}`} onClick={() => setLevel(t.key, v)}
                                className={`px-3 py-1 font-semibold transition-colors duration-200 ${lvl === v ? (v === "write" ? "bg-[#1F3C88] text-white" : "bg-[#575755] text-white") : "bg-white text-[#575755] hover:bg-[#F8FAFE]"}`}>
                                {v === "write" ? "Read/Write" : "Read Only"}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <label data-testid="can-manage-toggle" className={`flex items-center justify-between px-4 py-3 rounded-sm border cursor-pointer ${form.can_manage_users || adminSelected ? "border-[#F7941D] bg-[#FEF3E7]" : "border-[#E2E6F0]"}`}>
              <span className="text-sm"><ShieldCheck className="w-4 h-4 inline mr-1.5 text-[#F7941D]" />Can manage users (add people & assign access)</span>
              <input type="checkbox" disabled={adminSelected} checked={adminSelected || form.can_manage_users} onChange={(e) => setForm({ ...form, can_manage_users: e.target.checked })} className="accent-[#F7941D] w-4 h-4" />
            </label>

            <label data-testid="weekend-digest-toggle" className={`flex items-center justify-between px-4 py-3 rounded-sm border cursor-pointer ${form.weekend_task_digest ? "border-[#1F3C88] bg-[#F4F7FD]" : "border-[#E2E6F0]"}`}>
              <span className="text-sm"><CalendarClock className="w-4 h-4 inline mr-1.5 text-[#1F3C88]" />Weekend task digest (Fri 10am ET — Sat &amp; Sun tasks on their files)</span>
              <input type="checkbox" checked={!!form.weekend_task_digest} onChange={(e) => setForm({ ...form, weekend_task_digest: e.target.checked })} className="accent-[#1F3C88] w-4 h-4" />
            </label>

            {editing && (
              <button type="button" data-testid="resend-welcome-button" onClick={resendWelcome} disabled={resendingWelcome}
                className="w-full py-2.5 rounded-full border border-[#1F3C88] text-[#1F3C88] font-semibold hover:bg-[#F8FAFE] transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60">
                {resendingWelcome ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {resendingWelcome ? "Sending…" : "Resend Welcome Email"}
              </button>
            )}
            <button data-testid="user-submit-button" className="w-full py-3 rounded-full bg-[#1F3C88] text-white font-semibold hover:bg-[#152A61] transition-colors duration-200">{editing ? "Save Changes" : "Create User"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const inp = "w-full px-4 py-2.5 bg-white border border-[#E2E6F0] rounded-sm focus:ring-2 focus:ring-[#F7941D] focus:border-transparent focus:outline-none transition-colors duration-200";
function Field({ label, children }) {
  return (<div><label className="text-xs tracking-[0.15em] uppercase font-semibold text-[#555] block mb-1.5">{label}</label>{children}</div>);
}
